import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { parseFormattedText } from '@/lib/textFormatting';
import { ModeToggle } from '@/components/mode-toggle';
import { MobileMenuButton } from '@/components/MobileMenuButton';
import { EvidenceMetricsCarousel } from '@/components/EvidenceMetricsCarousel';
import { apiClient } from '@/api/client';
import {
  getCloudIncidentTypeLabel,
  getIncidentTypeLabel,
  getSeverityBadgeVariant,
  getTimelineIcon,
} from './shared/incidentView/helpers';
import {
  ReadOnlyCloudCliFixCard,
  ReadOnlyCodeFixCard,
  ReadOnlyYAMLFixCard,
} from './shared/incidentView/ReadOnlyFixCards';
import {
  adaptWorkflowRCA,
  type AdaptedRCAView,
  type WorkflowRCAStepOutput,
} from './shared/incidentView/adaptWorkflowRCA';
import { getRiskLevelBadgeVariant } from './shared/incidentView/helpers';
import { ServiceDependencyGraph } from './shared/ServiceDependencyGraph';

// Flatten step_results (which may contain nested arrays) and find by step id.
function findStepResult(stepResults: any[] | undefined, stepId: string): any | null {
  if (!Array.isArray(stepResults)) return null;
  const flat = stepResults.flatMap((s: any) => (Array.isArray(s) ? s : [s]));
  return flat.find((s: any) => s && s.step_id === stepId) || null;
}

// Format an ISO timestamp string (workflow step timestamps are ISO strings,
// unlike incident timestamps which are Unix seconds).
function formatIso(value?: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

// Detection-signal timestamps may be Unix seconds (backend) or ISO strings
// (trigger-signal fallback). Render a short time label, or null if absent.
function formatSignalTime(value?: number | string): string | null {
  if (value === undefined || value === null || value === '') return null;
  const d =
    typeof value === 'number'
      ? new Date(value * 1000)
      : /^\d+$/.test(value)
        ? new Date(parseInt(value, 10) * 1000)
        : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

export default function WorkflowRCADetail() {
  const { id, execId, stepId } = useParams<{ id: string; execId: string; stepId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<AdaptedRCAView | null>(null);
  const [showAllChainOfEvents, setShowAllChainOfEvents] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!execId || !stepId) {
        setError('Missing execution or step identifier.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const execution = await apiClient.getWorkflowExecution(execId);
        const step = findStepResult(execution.step_results, stepId);
        if (!step) {
          setError('Could not find the RCA step in this execution.');
          setLoading(false);
          return;
        }
        const output = (step.output || {}) as WorkflowRCAStepOutput;
        if (!output.rca_result) {
          setError('This step does not contain root cause analysis results.');
          setLoading(false);
          return;
        }
        const adapted = adaptWorkflowRCA(output, step.action, {
          startedAt: step.started_at,
          completedAt: step.completed_at,
          clusterName: (execution.trigger_signal?.cluster_name as string) || undefined,
          clusterId:
            (output.rca_result?.cluster_id as string) ||
            (execution.trigger_signal?.cluster_id as string) ||
            undefined,
          triggerSignal: execution.trigger_signal || undefined,
        });
        if (!cancelled) {
          setView(adapted);
          setLoading(false);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load RCA details.');
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [execId, stepId]);

  const backToExecution = () => navigate(`/workflows/${id}/executions/${execId}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !view) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={backToExecution} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to execution
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || 'No RCA details available.'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const { rca, source, severity, incidentType } = view;
  const typeLabel =
    source === 'cloud' ? getCloudIncidentTypeLabel(incidentType) : getIncidentTypeLabel(incidentType);
  const investigationSummary =
    rca.investigation_summary && typeof rca.investigation_summary === 'object'
      ? rca.investigation_summary
      : null;
  const evidenceMetrics: any[] = Array.isArray(rca.evidence_metrics) ? rca.evidence_metrics : [];

  return (
    <div className="flex flex-col -mx-4 sm:-mx-6 lg:-mx-8 -my-4 overflow-hidden" style={{ height: '100dvh' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 py-4 border-b border-border bg-background">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-4 min-w-0">
            <MobileMenuButton />
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold tracking-tight min-w-0">{typeLabel}</h1>
                <Badge variant={getSeverityBadgeVariant(severity)}>{severity}</Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Root cause analysis generated by this workflow step
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <ModeToggle />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 lg:px-8 py-6 min-w-0">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Root Cause */}
            {rca.root_cause && (
              <Card>
                <CardHeader>
                  <CardTitle>Root Cause Analysis</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-muted-foreground mb-2">Incident Type</h4>
                    <p className="text-base break-words">{typeLabel}</p>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-semibold text-muted-foreground mb-2">Root Cause</h4>
                    <p className="text-base leading-relaxed break-words">
                      {parseFormattedText(rca.root_cause)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Investigation Summary (K8s structured shape) */}
            {investigationSummary && investigationSummary.incident_summary && (
              <Card>
                <CardHeader>
                  <CardTitle>Investigation Summary</CardTitle>
                  <CardDescription>Detailed analysis of the incident and chain of events</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {investigationSummary.incident_summary?.text && (
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-3">Incident Summary</h3>
                      <p className="text-base leading-relaxed text-foreground break-words">
                        {parseFormattedText(investigationSummary.incident_summary.text)}
                      </p>
                    </div>
                  )}

                  {Array.isArray(investigationSummary.timeline_of_events) &&
                    investigationSummary.timeline_of_events.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-3">Chain of Events</h3>
                        <div className="space-y-2">
                          {investigationSummary.timeline_of_events
                            .slice(0, showAllChainOfEvents ? undefined : 10)
                            .map((event: any, idx: number) => (
                              <div key={idx}>
                                {event.time_before && (
                                  <div className="flex items-center justify-center my-2">
                                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                                      <div className="h-4 w-0.5 bg-border" />
                                      <span>↓ {event.time_before}</span>
                                    </div>
                                  </div>
                                )}
                                <div className="flex items-start gap-3 p-3 rounded-md border bg-card hover:bg-muted/50 transition-colors">
                                  <div className="flex-shrink-0 mt-0.5">{getTimelineIcon(event.icon)}</div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2 mb-1">
                                      <span className="text-xs font-mono text-muted-foreground">{event.time}</span>
                                      <span className="text-sm font-semibold text-foreground">{event.label}</span>
                                    </div>
                                    <p className="text-sm text-foreground leading-relaxed break-words">
                                      {parseFormattedText(event.description)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          {investigationSummary.timeline_of_events.length > 10 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full text-muted-foreground hover:text-foreground"
                              onClick={() => setShowAllChainOfEvents(!showAllChainOfEvents)}
                            >
                              <ChevronDown
                                className={`h-4 w-4 mr-1 transition-transform ${showAllChainOfEvents ? 'rotate-180' : ''}`}
                              />
                              {showAllChainOfEvents
                                ? 'Show less'
                                : `Show ${investigationSummary.timeline_of_events.length - 10} more events`}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                  {investigationSummary.root_cause?.summary && (
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-3">Root Cause</h3>
                      <div className="border bg-card p-4 rounded-md space-y-3">
                        <p className="text-base font-semibold text-foreground">
                          {parseFormattedText(investigationSummary.root_cause.summary)}
                        </p>
                        {Array.isArray(investigationSummary.root_cause.details) && (
                          <ul className="space-y-2 list-disc list-inside">
                            {investigationSummary.root_cause.details.map((detail: string, idx: number) => (
                              <li key={idx} className="text-sm leading-relaxed text-foreground">
                                {parseFormattedText(detail)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}

                  {Array.isArray(investigationSummary.affected_resources) &&
                    investigationSummary.affected_resources.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-3">Affected Resources</h3>
                        <div className="grid gap-2">
                          {investigationSummary.affected_resources.map((resource: any, idx: number) => (
                            <div key={idx} className="flex items-start gap-3 p-3 rounded-md border bg-card">
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="outline" className="text-xs">
                                    {resource.type}
                                  </Badge>
                                  <span className="font-mono text-sm break-all">
                                    {resource.namespace}/{resource.name}
                                  </span>
                                </div>
                                {resource.status && (
                                  <span className="text-xs text-muted-foreground mt-1 block">{resource.status}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {investigationSummary.resolution_recommendation?.primary && (
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-3">Resolution Recommendation</h3>
                      <div className="border bg-card p-4 rounded-md space-y-4">
                        <div className="flex items-start gap-2">
                          <p className="text-base font-semibold text-foreground">
                            {parseFormattedText(investigationSummary.resolution_recommendation.primary)}
                          </p>
                        </div>
                        {Array.isArray(investigationSummary.resolution_recommendation.steps) && (
                          <div>
                            <h4 className="text-sm font-semibold text-foreground mb-2">Steps:</h4>
                            <ol className="space-y-2 list-decimal list-inside">
                              {investigationSummary.resolution_recommendation.steps.map(
                                (step: string, idx: number) => (
                                  <li key={idx} className="text-sm leading-relaxed text-foreground">
                                    {parseFormattedText(step)}
                                  </li>
                                ),
                              )}
                            </ol>
                          </div>
                        )}
                        {investigationSummary.resolution_recommendation.prevention && (
                          <div className="pt-3 border-t">
                            <h4 className="text-sm font-semibold text-foreground mb-2">Prevention:</h4>
                            <p className="text-sm leading-relaxed text-foreground">
                              {parseFormattedText(investigationSummary.resolution_recommendation.prevention)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Stack trace (application-level failures) */}
            {rca.stack_trace_summary && (
              <Card>
                <CardHeader>
                  <CardTitle>Stack Trace</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="p-3 rounded-md bg-muted border font-mono text-xs overflow-x-auto max-h-64 whitespace-pre-wrap">
                    {rca.stack_trace_summary}
                  </pre>
                </CardContent>
              </Card>
            )}

            {/* Evidence metrics */}
            {evidenceMetrics.length > 0 && <EvidenceMetricsCarousel metrics={evidenceMetrics} />}

            {/* YAML fixes (K8s) */}
            {view.yamlFixes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Remediation ({view.yamlFixes.length})</CardTitle>
                  <CardDescription>YAML manifest changes to resolve this incident</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {view.yamlFixes.map((fix, index) => (
                    <ReadOnlyYAMLFixCard key={index} fix={fix} index={index} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Cloud CLI fixes */}
            {view.cloudCliFixes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Remediation ({view.cloudCliFixes.length})</CardTitle>
                  <CardDescription>CLI commands to resolve this incident</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {view.cloudCliFixes.map((fix, index) => (
                    <ReadOnlyCloudCliFixCard
                      key={index}
                      fix={fix}
                      index={index}
                      riskBadgeVariant={getRiskLevelBadgeVariant(fix.risk_level || '')}
                    />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Terraform / IaC fixes (cloud) */}
            {view.terraformFixes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>IaC Fixes ({view.terraformFixes.length})</CardTitle>
                  <CardDescription>Terraform changes to resolve this incident</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {view.terraformFixes.map((fix, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono text-xs">
                          {fix.file_path}
                        </Badge>
                      </div>
                      {fix.explanation && (
                        <p className="text-sm text-muted-foreground">{parseFormattedText(fix.explanation)}</p>
                      )}
                      <pre className="p-4 rounded-md border font-mono text-xs overflow-x-auto max-h-96 bg-muted">
                        <code className="text-foreground">{fix.content}</code>
                      </pre>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Code fixes */}
            {view.codeFixes.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Code Fixes ({view.codeFixes.length})</CardTitle>
                  <CardDescription>Application code changes to resolve this incident</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {view.codeFixes.map((fix, index) => (
                    <ReadOnlyCodeFixCard key={index} fix={fix} index={index} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Fallback when no fixes were generated */}
            {view.yamlFixes.length === 0 &&
              view.cloudCliFixes.length === 0 &&
              view.terraformFixes.length === 0 &&
              view.codeFixes.length === 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>No remediation fixes were generated for this incident.</AlertDescription>
                </Alert>
              )}

            {/* Service Dependency Graph (K8s only). Renders live cluster flow data
                when available; self-hides otherwise. */}
            {source !== 'cloud' && view.clusterId && (
              <ServiceDependencyGraph
                clusterId={view.clusterId}
                signals={Array.isArray(rca.detection_signals) ? rca.detection_signals : []}
                affectedResources={view.affectedResources}
              />
            )}
          </div>

          {/* Right column: compact metadata */}
          <div className="space-y-6 min-w-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {formatIso(view.detectedAt) && (
                  <div>
                    <span className="text-muted-foreground">Detected</span>
                    <div className="font-medium mt-1 break-words">{formatIso(view.detectedAt)}</div>
                  </div>
                )}
                {formatIso(view.completedAt) && (
                  <div>
                    <span className="text-muted-foreground">Analysis Completed</span>
                    <div className="font-medium mt-1 break-words">{formatIso(view.completedAt)}</div>
                  </div>
                )}
                {(view.detectedAt || view.completedAt) && <Separator />}
                {source === 'cloud' && view.accountId && (
                  <div>
                    <span className="text-muted-foreground">Account</span>
                    <div className="mt-1 font-medium break-all">{view.accountId}</div>
                  </div>
                )}
                {source !== 'cloud' && view.clusterName && (
                  <div>
                    <span className="text-muted-foreground">Cluster</span>
                    <div className="mt-1 font-medium break-words">{view.clusterName}</div>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Type</span>
                  <div className="mt-1 font-medium break-words">{typeLabel}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Severity</span>
                  <div className="mt-1">
                    <Badge variant={getSeverityBadgeVariant(severity)}>{severity}</Badge>
                  </div>
                </div>
                {rca.is_application_level_failure && (
                  <>
                    <Separator />
                    <div className="text-xs bg-muted/50 text-foreground border border-border p-2 rounded">
                      Application code issue detected
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Affected Resources */}
            {view.affectedResources.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Affected Resources ({view.affectedResources.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {view.affectedResources.map((resource: any, idx: number) => (
                    <div key={idx} className="flex items-start gap-2 p-2 rounded-md border bg-card">
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2 flex-wrap">
                          {resource.type && (
                            <Badge variant="outline" className="text-xs">
                              {resource.type}
                            </Badge>
                          )}
                          <span className="font-mono text-xs break-all">
                            {[resource.namespace, resource.name].filter(Boolean).join('/') ||
                              resource.name ||
                              resource.identifier ||
                              'resource'}
                          </span>
                        </div>
                        {resource.status && (
                          <span className="text-xs text-muted-foreground mt-1 block">{resource.status}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Detection Signals */}
            {view.detectionSignals.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Detection Signals ({view.detectionSignals.length})</CardTitle>
                  <CardDescription className="text-xs">
                    {source === 'cloud'
                      ? 'Cloud events that triggered this analysis'
                      : 'Kubernetes events and status changes that triggered this analysis'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {view.detectionSignals.map((signal, idx) => (
                      <div key={idx} className="p-3 rounded-md border bg-card text-xs space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          {signal.signal_type && (
                            <Badge variant="outline" className="text-xs font-mono break-all">
                              {signal.signal_type}
                            </Badge>
                          )}
                          {formatSignalTime(signal.timestamp) && (
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {formatSignalTime(signal.timestamp)}
                            </span>
                          )}
                        </div>
                        {signal.reason && <div className="font-medium break-words">{signal.reason}</div>}
                        {signal.message && (
                          <div className="text-muted-foreground line-clamp-2 break-words">{signal.message}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
